import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { LoggingService } from '@tdoe/design-system';
import { combineLatest, interval, map, of, tap, timer } from 'rxjs';
import { GalleryItem } from './gallery-item';

@Component({
  selector: 'app-rotating-gallery',
  standalone: true,
  imports: [
    CommonModule,
    MatGridListModule
  ],
  templateUrl: './rotating-gallery.html',
  styleUrl: './rotating-gallery.scss',
})
export class RotatingGallery {

  private readonly logger = inject(LoggingService);

  private currentImages: GalleryItem[] = [];
  private currentIndex = 0;

  public readonly interval = input<number>(2500);

  public readonly viewModel$ = combineLatest([
    of([
      '/spotlight/1.jpg',
      '/spotlight/2.jpg',
      '/spotlight/3.jpg',
      '/spotlight/4.jpg',
      '/spotlight/5.jpg',
      '/spotlight/6.jpg',
      '/spotlight/7.jpg',
      '/spotlight/8.jpg',
      '/spotlight/9.jpg',
      '/spotlight/10.jpg',
      '/spotlight/11.jpg',
      '/spotlight/12.jpg',
      '/spotlight/13.jpg',
      '/spotlight/14.jpg',
      '/spotlight/15.jpg',
      '/spotlight/16.jpg',
      '/spotlight/17.jpg',
      '/spotlight/18.jpg',
      '/spotlight/19.jpg',
      '/spotlight/20.jpg',
      '/spotlight/21.jpg',
      '/spotlight/22.jpg',
      '/spotlight/23.jpg',
      '/spotlight/24.jpg',
      '/spotlight/25.jpg',
      '/spotlight/26.jpg',
      '/spotlight/27.jpg',
      '/spotlight/28.jpg',
      '/spotlight/29.jpg',
      '/spotlight/30.jpg',
    ]),
    interval(this.interval())
  ]).pipe(
    map(([galleryImages]) => ({galleryImages})),
    map(data => ({
      galleryImages: data.galleryImages,
      imageSlots: this.getImageRotation(data.galleryImages),
      currentIndex: this.currentIndex
    })),
    tap(vm => {
      this.logger.debug('RotatingGallery -> viewModel$', { data: vm });
    })
  );

  private getImageRotation(gallery: string[]): GalleryItem[] {
    if (this.currentImages.length === 0) {
      return this.initializeImages(gallery);
    } else {
      const usedImages = new Set(
        this.currentImages.flatMap(_ => [_.current, _.next]).filter(Boolean)
      );
      let unusedImages = gallery.filter(_ => !usedImages.has(_));
      const newCurrent = this.getRandomImage(unusedImages);
      usedImages.add(newCurrent);
      this.currentImages[this.currentIndex].current = newCurrent;
      this.incrementIndex();
      unusedImages = gallery.filter(_ => !usedImages.has(_));
      this.currentImages[this.currentIndex].next = this.getRandomImage(unusedImages);
      this.currentImages[this.currentIndex].current = (undefined as any);
      return this.currentImages;
    }
  }

  private incrementIndex(): void {
    this.currentIndex++;
    if (this.currentIndex >= this.currentImages.length) {
      this.currentIndex = 0;
    }
  }

  private getRandomImage(available: string[]): string {
    const randomIndex = Math.floor(Math.random() * available.length);
    return available[randomIndex];
  }

  private initializeImages(gallery: string[]): GalleryItem[] {
    const result: GalleryItem[] = [];
    const usedImages = new Set<string>();

    while (result.length < 12) {
      let unusedImages = gallery.filter(_ => !usedImages.has(_));
      const current = this.getRandomImage(unusedImages);
      usedImages.add(current);
      unusedImages = gallery.filter(_ => !usedImages.has(_));
      const next = this.getRandomImage(unusedImages);
      usedImages.add(next);
      result.push({ current, next });
    }

    return this.currentImages = result;
  }

}
